import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import Loading from "../Loading";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, useTable } from "../components/Table/Table";
import { formatCurrency } from "../lib/numbers";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import {
  useSlantConnected,
  makeServiceTierColumn,
} from "../components/Table/serviceTier";
import {
  RebalanceReasonFilter,
  RebalanceRow,
  getRealizedGainLoss,
  getRiskAllocationDisplay,
  mapRebalancesToRebalanceRows,
  rebalanceTypeDisplay,
  rebalanceTypeFilterOptions,
} from "./lib";
import { deserializeRebalanceListPercents } from "./percents";
import { multiSelectIncludes } from "../components/Table/filters";

const columnHelper = createColumnHelper<RebalanceRow>();

/**
 * Remounts the table per household, so the rebalances the page walk collected
 * for one household are never shown under another (`lib/pagination.ts`).
 *
 * `name` is the accessible name of the box that scrolls the table, which is a
 * named tab stop while the table is wider than it (R183, APP-2226).
 */
const RebalanceExecutedTable = ({
  householdId,
  name,
}: {
  householdId?: string;
  name?: string;
}) => (
  <RebalanceExecutedTableOfHousehold
    key={householdId}
    householdId={householdId}
    name={name}
  />
);

const RebalanceExecutedTableOfHousehold = ({
  householdId,
  name,
}: {
  householdId?: string;
  name?: string;
}) => {
  // `GET /v1/rebalances/executed` answers a page (APP-1652); the walk in
  // `useAuthenticatedFetchAllPages` keeps asking until it holds every rebalance
  // `pagination.totalCount` reports. `householdId` narrows the rows on the
  // server, before the page is taken.
  const {
    rows: pageRows,
    hasEveryRow,
    isError,
  } = useAuthenticatedFetchAllPages<
    UnpackResponse<
      RebalancesController["getAllExecutedRebalances"]
    >["data"][number]
  >(
    "/v1/rebalances/executed",
    typeof householdId === "undefined"
      ? { metrics: "true" }
      : { metrics: "true", householdId },
    { enabled: typeof householdId !== "undefined" },
  );
  const isPendingRebalances = !hasEveryRow && !isError;
  const rebalances = useMemo(
    () =>
      hasEveryRow ? deserializeRebalanceListPercents(pageRows) : undefined,
    [hasEveryRow, pageRows],
  );

  const slantConnected = useSlantConnected();

  const rebalanceRows = useMemo(
    () => mapRebalancesToRebalanceRows(rebalances),
    [rebalances],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) =>
          row.level === "account" || typeof row.type === "undefined"
            ? ""
            : (row.actualExecutionDate?.toLocaleString() ?? ""),
        {
          id: "executionTime",
          cell: (info) => (
            <div
              style={{
                // Since rows are flattened by default,
                // we can use the row.depth property
                // and paddingLeft to visually indicate the depth
                // of the row
                paddingLeft: `${info.row.depth * 2}rem`,
              }}
            >
              {info.row.original.name}
            </div>
          ),
          aggregatedCell: (info) => (
            <>
              {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
              <Link to={`/rebalances/${info.row.original.rebalanceId}`}>
                {info.getValue()}
              </Link>
            </>
          ),
          header: () => "Execution Time / Account",
          minSize: 225,
          enableColumnFilter: false,
        },
      ),
      // Service Tier (Slant) — immediately after the household/name column;
      // omitted entirely for a non-Slant firm (F4).
      ...(slantConnected ? [makeServiceTierColumn(columnHelper)] : []),
      columnHelper.accessor(
        (row: RebalanceRow) => {
          if (row.level === "account" || typeof row.type === "undefined") {
            return "";
          } else if (
            row.type &&
            Object.prototype.hasOwnProperty.call(rebalanceTypeDisplay, row.type)
          ) {
            const option = rebalanceTypeFilterOptions.find(
              (a) =>
                a.label ===
                rebalanceTypeDisplay[
                  row.type as keyof typeof rebalanceTypeDisplay
                ],
            );
            return option?.value.toString() ?? "";
          }
          return "";
        },
        {
          id: "type",
          header: () => "Trade Proposal Reason",
          cell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          aggregatedCell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: RebalanceReasonFilter,
          },
        },
      ),
      columnHelper.accessor((row) => row.riskAllocation?.target.growth ?? 0, {
        id: "target",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
        header: () => "Target Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor((row) => row.riskAllocation?.current.growth ?? 0, {
        id: "current",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        header: () => "Current Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor(
        (row) => row.riskAllocation?.postRebalance.growth ?? 0,
        {
          id: "postRebalance",
          cell: (info) =>
            getRiskAllocationDisplay(
              info.row.original.riskAllocation?.postRebalance,
            ),
          aggregatedCell: (info) =>
            getRiskAllocationDisplay(
              info.row.original.riskAllocation?.postRebalance,
            ),
          header: () => "Post-Trade Proposal Allocation",
          enableColumnFilter: false,
          enableSorting: false,
          meta: { className: "text-nowrap" },
        },
      ),
      columnHelper.accessor((row) => row.totalBalance ?? 0, {
        id: "totalBalance",
        cell: (info) => formatCurrency(info.row.original.totalBalance),
        aggregatedCell: (info) =>
          formatCurrency(info.row.original.totalBalance),
        header: () => "AUM",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.cashBalance ?? 0, {
        id: "cashBalance",
        cell: (info) => formatCurrency(info.row.original.cashBalance),
        aggregatedCell: (info) => formatCurrency(info.row.original.cashBalance),
        header: () => "Cash",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.buyTotal ?? 0, {
        id: "buyTotal",
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Buy",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.sellTotal ?? 0, {
        id: "sellTotal",
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Sell",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor(
        (row) => (row.sellTotal ?? 0) - (row.buyTotal ?? 0),
        {
          id: "net",
          cell: (info) => formatCurrency(info.getValue()),
          aggregatedCell: (info) => formatCurrency(info.getValue()),
          header: () => "Net",
          enableColumnFilter: false,
          meta: {
            headerClassName: "text-end",
            className: "text-end",
          },
        },
      ),
      columnHelper.accessor(
        (row) => (row.realizedGainLossSt ?? 0) - (row.realizedGainLossLt ?? 0),
        {
          id: "realizedGainLoss",
          // "—" when both are `null`, as `getRealizedGainLoss` answers.
          cell: (info) =>
            getRealizedGainLoss(info.row.original) == null
              ? formatCurrency(null)
              : formatCurrency(info.getValue()),
          aggregatedCell: (info) =>
            getRealizedGainLoss(info.row.original) == null
              ? formatCurrency(null)
              : formatCurrency(info.getValue()),
          header: () => "Realized Gain/Loss",
          enableColumnFilter: false,
          meta: {
            headerClassName: "text-end",
            className: "text-end",
          },
        },
      ),
    ],
    [slantConnected],
  );

  const { table } = useTable({
    columns,
    data: rebalanceRows,
    getRowId: (row) => `${row.rebalanceId}-${row.level}-${row.id}`,
    getSubRows: (row) => row.accounts ?? [],
    autoResetPageIndex: true,
    initialState: {
      sorting: [{ id: "executionTime", desc: true }],
    },
  });

  return (
    <>
      {isPendingRebalances ? (
        <Loading />
      ) : rebalances?.length === 0 ? (
        <Alert variant="info">No Rebalance history found.</Alert>
      ) : (
        <Table table={table} showSearchGroup={false} name={name} />
      )}
    </>
  );
};

export default RebalanceExecutedTable;
